// Dates and times (DESIGN_SYSTEM §4.8). The timezone is always Asia/Kathmandu (UTC+05:45, no DST);
// day boundaries for "today" and reports are Nepal-local. AD is the default. Bikram Sambat display is
// an optional per-store setting (`bs_dates_enabled`, default off, D-013/D-027): P4-C's
// `packages/calendar-bs` adapter plugs in through `registerCalendarAdapter`. Without an adapter, BS
// is never guessed: the AD date is shown alone.

export const NEPAL_TIME_ZONE = "Asia/Kathmandu";
const LOCALE = "en-IN";

export type DateStyle = "short" | "medium" | "long" | "weekday";

export interface CalendarAdapter {
  /** Stable identifier, e.g. "bs". */
  readonly id: string;
  /**
   * Formats the Nepal-local calendar day of `date` in the adapter's calendar, for example
   * "Kartik 1, 2083 BS". Must throw (not guess) when the date is outside its verified data range.
   */
  formatDate(date: Date, style: DateStyle): string;
}

let adapter: CalendarAdapter | null = null;

/** Registers the secondary-calendar adapter (call once at app start). Pass null to remove it. */
export function registerCalendarAdapter(next: CalendarAdapter | null): void {
  adapter = next;
}
export function getCalendarAdapter(): CalendarAdapter | null {
  return adapter;
}

export interface DateFormatOptions {
  style?: DateStyle;
  /** The store setting `bs_dates_enabled` (SETTINGS-001). Default off. */
  bsDatesEnabled?: boolean;
}

const dateFormats: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: "numeric", month: "short" },
  medium: { day: "numeric", month: "short", year: "numeric" },
  long: { weekday: "long", day: "numeric", month: "short", year: "numeric" },
  weekday: { weekday: "long", day: "numeric", month: "short" },
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();
function dtf(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(LOCALE, { timeZone: NEPAL_TIME_ZONE, ...options });
    formatterCache.set(key, f);
  }
  return f;
}

function toDate(value: Date | string | number): Date {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) throw new RangeError(`invalid date: ${String(value)}`);
  return d;
}

/** The AD date only, in Nepal time: "18 Oct 2026", "Sunday, 18 Oct 2026". */
export function formatDateAD(value: Date | string | number, style: DateStyle = "medium"): string {
  return dtf(dateFormats[style]).format(toDate(value));
}

/**
 * The display hook every surface uses. With BS enabled and an adapter registered it returns
 * "18 Oct 2026 (Kartik 1, 2083 BS)"; otherwise the AD date alone.
 */
export function formatDate(value: Date | string | number, options: DateFormatOptions = {}): string {
  const style = options.style ?? "medium";
  const d = toDate(value);
  const ad = formatDateAD(d, style);
  if (!options.bsDatesEnabled || !adapter) return ad;
  try {
    return `${ad} (${adapter.formatDate(d, style)})`;
  } catch {
    return ad; // Outside the adapter's verified range: never show a guessed BS date.
  }
}

/** "9:15 am" in Nepal time. */
export function formatTime(value: Date | string | number): string {
  return dtf({ hour: "numeric", minute: "2-digit", hour12: true }).format(toDate(value));
}

/** "18 Oct 2026, 9:15 am". */
export function formatDateTime(
  value: Date | string | number,
  options: DateFormatOptions = {},
): string {
  return `${formatDate(value, options)}, ${formatTime(value)}`;
}

/** Nepal-local calendar day key, "2026-10-18", for grouping and "today" boundaries. */
export function nepalDayKey(value: Date | string | number): string {
  const parts = dtf({ year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(
    toDate(value),
  );
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "narrow" });

/**
 * Relative time for activity under 24 h ("now", "5m ago", "2h ago"); older values fall back to the
 * short date ("18 Oct"), per DESIGN_SYSTEM §4.8.
 */
export function formatRelative(value: Date | string | number, now: Date = new Date()): string {
  const d = toDate(value);
  const diffS = Math.round((d.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffS);
  if (abs < 45) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(diffS / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(diffS / 3600), "hour");
  return formatDateAD(
    d,
    nepalDayKey(d).slice(0, 4) === nepalDayKey(now).slice(0, 4) ? "short" : "medium",
  );
}
